import { useEffect } from 'react';

import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import { useHiddenBodyOverflow } from '../../hooks/useHiddenBodyOverflow';
import { useStartGame } from '../../hooks/useStartGame';
import { analytics } from '../../services/analytics';
import { logToNewRelic } from '../../services/debug';
import { useStores } from '../../stores/useStores';
import { devError } from '../../utils/log';
import Button from '../buttons/Button';
import { FRBWidget } from '../FRBWidget/FRBWidget';
import { TextWithCrowns } from '../TextWithCrown/TextWithCrown';

import classes from './FreeSpinDialog.module.scss';
import { getTextsByMinigameType } from './utils';

const freeSpinsProviders = {
  RUBY_PLAY: 'RUBY_PLAY',
  PRAGMATIC_PLAY: 'PRAGMATIC_PLAY',
  BAROULETTA: 'Barouletta Gaming',
  SPINOMENAL: 'Spinomenal',
  MINIGAMES: 'Minigames',
};

// Identity on every pop-up event; the shown game rides the game_id pair when the free spins carry one.
const freeSpinsEvent = (gameId?: number) => ({
  event_parent: 'features' as const,
  event_category: 'free_spins' as const,
  page_url: {},
  ...(gameId ? { event_type: 'game_id' as const, event_value: String(gameId) } : {}),
});

export const FreeSpinDialog = observer(() => {
  const { t } = useTranslation();
  const { userFreeSpinsStore, minigamesStore, featureManagerStore } = useStores();
  const { selectedFreeSpin, selectedFreeSpinSource } = userFreeSpinsStore;

  const { handleStartGame } = useStartGame();

  useHiddenBodyOverflow(true);

  // One impression per free spins shown.
  const freeSpinId = selectedFreeSpin?.id;
  const shownGameId = selectedFreeSpin?.gameId;
  useEffect(() => {
    if (!freeSpinId) return;
    void analytics.sendEvent('popup', {
      ...freeSpinsEvent(shownGameId),
      event_action: 'impression',
      triggered_by: selectedFreeSpinSource ?? undefined,
    });
  }, [freeSpinId, shownGameId, selectedFreeSpinSource]);

  if (!selectedFreeSpin) {
    return null;
  }

  const nextDialogHandler = () => {
    const isNextDialog = userFreeSpinsStore.showNextDialog();
    if (!isNextDialog) {
      featureManagerStore.onDialogFinished('UserFreeSpins');
    }
  };

  const onCloseHandler = () => {
    void analytics.sendEvent('popup_close', { ...freeSpinsEvent(shownGameId), event_action: 'click' });
    userFreeSpinsStore.closeDialog();
    setTimeout(nextDialogHandler, 500);
  };

  const onClaimHandler = () => {
    void analytics.sendEvent('claim', { ...freeSpinsEvent(shownGameId), event_action: 'click', button_text: 'claim' });
    const claimedSpinId = selectedFreeSpin.id;
    let claimed = false;
    try {
      if ([freeSpinsProviders.BAROULETTA, freeSpinsProviders.MINIGAMES].includes(selectedFreeSpin.provider)) {
        if (selectedFreeSpin.gameId) {
          const gameId = Number(selectedFreeSpin.gameId);
          void minigamesStore.startMinigame(gameId, nextDialogHandler);
        } else {
          throw new Error(`Cannot start barouletta game`);
        }
      } else {
        if (selectedFreeSpin.gameId && selectedFreeSpin.coinType) {
          handleStartGame(selectedFreeSpin.gameId, 'popup', selectedFreeSpin.coinType, 'free_spins');
        } else {
          throw new Error(
            `Cannot start game. Game ID: ${selectedFreeSpin.gameId || 'N/A'}, Coin Type: ${selectedFreeSpin.coinType || 'N/A'}`,
          );
        }
      }
      claimed = true;
    } catch (error: any) {
      devError('Error starting free spin game', error);
      void logToNewRelic('Error starting free spin game', { message: error.message, selectedFreeSpin }, 'error');
    }
    userFreeSpinsStore.closeDialog();
    if (claimed) {
      userFreeSpinsStore.markClaimed(claimedSpinId);
    }
  };

  const texts = getTextsByMinigameType(selectedFreeSpin.provider, selectedFreeSpin?.gameType, selectedFreeSpin.type);
  const isAwardedFeature = selectedFreeSpin.type === 'AWARDED_FEATURE';

  return (
    <div className={classes.freeSpinDialogCover}>
      <div className={classes.dialogContent}>
        <div className={classes.closeWrapper}>
          <div onClick={onCloseHandler}>
            <div className={classes.closeBtn}>&times;</div>
          </div>
        </div>
        <TextWithCrowns
          crownFill={isAwardedFeature ? '#B818FE' : '#fff'}
          crownLetters={texts.titleCrownLetters}
          crownStyle={isAwardedFeature ? { bottom: '80%', width: '0.6em' } : { bottom: '80%' }}
          style={{
            fontSize: texts.fontSize ? texts.fontSize : '41px',
            fontWeight: '900',
            lineHeight: 1,
            overflow: 'visible',
          }}
          text={texts.title}
        />
        <div className={classes.title}>{texts.subTitle}</div>
        <div className={classes.widgetParent}>
          <FRBWidget
            betAmount={Number(selectedFreeSpin.bet)}
            coinType={selectedFreeSpin.coinType || 'GC'}
            freeSpinType={selectedFreeSpin.type}
            gameImage={selectedFreeSpin.gameImageUrl}
            height={isAwardedFeature ? 250 : undefined}
            maxPrize={selectedFreeSpin.maxPrize}
            provider={selectedFreeSpin.provider}
            rounds={Number(selectedFreeSpin.rounds) - Number(selectedFreeSpin.progress)}
            type="freeSpins"
            width={250}
          />
        </div>
        <Button
          btnType="main"
          className={classes.btn}
          disabled={minigamesStore.loadingMinigame}
          onClick={onClaimHandler}
        >
          {t('common.buttons.claim')}
        </Button>
      </div>
    </div>
  );
});
